import { lbBrand, lbGreen } from '@fuse/colors';
import {
  KeywordRankStatus,
  UberallAutoResponseStatus,
  UberallListingStatus,
  UberallSocialConnectionStatus,
  UberallSocialPostStatus,
  UberallTaskImpact,
} from '../type';

export const visibilityPalette = {
  brand: lbBrand.red,
  brandDark: lbBrand.buttonGradientDark,
  brandDarker: lbBrand.redDark,
  brandLight: lbBrand.redLight,
  brandWash: '#F3F5F8',
  brandWash2: '#E7EBF1',
  brandWash3: '#D5DEEA',

  ok: lbGreen.default,
  okWash: '#E2F4EE',

  warn: '#96590A',
  warnWash: '#FBEECF',
  info: '#2F73C7',
  infoWash: '#E2EDF9',
  idle: '#8894A0',
  idleWash: '#EEF1F3',
  crit: lbBrand.red,
  critWash: '#FBEAEA',
  star: '#F5A623',

  ink: lbBrand.black,
  ink2: lbBrand.darkGray,
  ink3: '#767676',
  border: lbBrand.border,
  borderStrong: lbBrand.neutral,
  surface2: lbBrand.surface,
} as const;

export type VisibilityTone = 'ok' | 'warn' | 'info' | 'idle' | 'crit';

export interface ToneColors {
  main: string;
  wash: string;
}

export const toneColors: Record<VisibilityTone, ToneColors> = {
  ok: { main: lbGreen.darker, wash: visibilityPalette.okWash },
  warn: { main: visibilityPalette.warn, wash: visibilityPalette.warnWash },
  info: { main: visibilityPalette.info, wash: visibilityPalette.infoWash },
  idle: { main: visibilityPalette.ink3, wash: visibilityPalette.idleWash },
  crit: { main: visibilityPalette.crit, wash: visibilityPalette.critWash },
};

export const LISTING_STATUS_TONE: Record<UberallListingStatus, VisibilityTone> = {
  SYNCED: 'ok',
  LINKED: 'ok',
  SUBMITTED: 'info',
  UPDATING: 'info',
  SCHEDULED: 'info',
  IN_PROGRESS: 'info',
  ACTION_REQUIRED: 'warn',
  NOT_STARTED: 'idle',
  UNKNOWN: 'idle',
};

export const TASK_IMPACT_TONE: Record<UberallTaskImpact, VisibilityTone> = {
  PRIORITY: 'crit',
  IMPORTANT: 'warn',
  RECOMMENDED: 'info',
  BONUS: 'idle',
};

export const AUTO_RESPONSE_STATUS_TONE: Record<UberallAutoResponseStatus, VisibilityTone> = {
  ACTIVE: 'ok',
  INACTIVE: 'idle',
  DELETED: 'idle',
};

export const POST_STATUS_TONE: Record<UberallSocialPostStatus, VisibilityTone> = {
  PUBLISHED: 'ok',
  ACTIVE: 'ok',
  SCHEDULED: 'info',
  APPROVAL_NEEDED: 'warn',
  INCOMPLETE: 'warn',
  PARTIALLY_PUBLISHED: 'warn',
  INACTIVE: 'idle',
  ENDED: 'idle',
  DELETED: 'idle',
  UNKNOWN: 'idle',
  IMPOSSIBLE: 'crit',
  REJECTED: 'crit',
  FAILED: 'crit',
};

export const SOCIAL_CONNECTION_STATUS_TONE: Record<UberallSocialConnectionStatus, VisibilityTone> = {
  CONNECTED: 'ok',
  NOT_CONNECTED: 'warn',
  NOT_NEEDED: 'idle',
  UNKNOWN: 'idle',
};

export const KEYWORD_RANK_TONE: Record<KeywordRankStatus, VisibilityTone> = {
  TOP1: 'ok',
  TOP3: 'ok',
  TOP10: 'info',
  BEYOND_TOP10: 'warn',
  UNRANKED: 'crit',
};

export const completenessTone = (score: number): VisibilityTone => {
  if (score >= 90) return 'ok';
  if (score >= 60) return 'warn';
  return 'crit';
};
